/**
 * AI先輩の「承認待ち」（issue #185・#109・決定23／2026-09-22）。
 *
 * ここに並ぶのは2種類です。
 *  - **教える部屋からの投稿**（現場の方が「AI先輩に教える」部屋に書いたもの）
 *  - **答えられなかった質問**（AI先輩が答えられず、まだ答えを書いていないもの）
 *
 * どちらも**承認して初めて** AI先輩の材料になります（docs/01 11-3・11-6）。
 * 1件開くと、元の投稿と、質問・答えの下書きを見ながら「承認する」「使わない」を選べます。
 *
 * 一度に読むのは50件ずつで、続きがあれば「さらに表示する」で読み足します（issue #238）。
 * 2種類は**サーバが受け取った日時の新しい順の1本の並びにして**、続きの位置も1つで返します
 * （`GET /admin/ai/pending`・issue #424）。以前は画面が2つの一覧を別々に読み足して混ぜていたため、
 * 読み足した行が一覧の途中に割り込んでいました。いまは読み足した分はいつも一覧の下に足されます。
 * 続きがあることは画面に言葉で出します（古いものが黙って見えなくならないように）。
 */
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import { usePagedList } from '../api/paged-list.js';
import { useSession } from '../auth/session-context.js';
import type { AiPendingItem, AiPendingPage } from '../api/types.js';
import {
  pendingKindLabel,
  pendingKindOf,
  sourceNotes,
  summarize,
  whoAndWhere,
  ASKER_NOT_SHOWN,
  ASKER_NOT_SHOWN_MARK,
  ASKER_NOT_SHOWN_NOTE,
  type PendingKind,
} from '../ai/labels.js';
import { LoadMoreFooter } from '../components/LoadMoreFooter.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { formatTableDateTime } from '../format/datetime.js';

/** 1回に読む件数（承認待ちは溜めずに片付けていただく想定）。 */
const PAGE_SIZE = 50;

/** 一覧に並べる1行。 */
interface PendingRow {
  key: string;
  kind: PendingKind;
  /** 何についてのものか（質問の下書き、または質問文） */
  title: string;
  /** 元の投稿の抜き書き（質問だけの行では空） */
  excerpt: string;
  /** 添えて伝えたいこと（写真つき・案内をお返しできていない・取り消された など） */
  notes: string[];
  who: string;
  /** どなたが聞いたかを出さない行（答えられなかった質問・issue #375・#387） */
  whoHidden?: boolean;
  receivedAt: string;
  to: string;
  /** その1件で次にすること（答えがまだ無い質問は「答えを書く」） */
  actionLabel: string;
}

export function AiPendingPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const siteScoped = profile?.grant !== undefined && profile.grant.scope !== 'org';
  const list = usePagedList<AiPendingItem>(
    'pending',
    async (cursor) => {
      const loaded = await api.get<AiPendingPage>('/admin/ai/pending', {
        query: { limit: PAGE_SIZE, ...(cursor !== null ? { cursor } : {}) },
      });
      return { items: loaded.items, next: loaded.nextCursor };
    },
    (item) => (item.type === 'draft' ? `k:${item.draft.id}` : `q:${item.question.id}`),
  );
  const rows = list.items ? list.items.map(toRow) : null;

  return (
    <article>
      <PageHeader
        title="承認待ち"
        description="現場から「AI先輩に教える」に届いたメッセージと、AI先輩が答えられなかった質問です。承認すると、AI先輩が出典つきで答えられるようになります。"
      />
      {/* 自拠点だけを受け持つ方にだけ出す（全社を受け持つ方には関係のない説明・#286 L-12） */}
      {siteScoped ? (
        <p className="admin-note">
          拠点を管理する方には、答えられなかった質問は所属拠点と受け持つ拠点の方の分だけが出ます。「AI先輩に教える」へのメッセージは全社の分が出ますが、受け持っていない拠点の方のお名前とトークの名前は伏せています（中身を読んで承認できます）。
        </p>
      ) : null}
      {rows?.some((row) => row.whoHidden) ? (
        <p className="admin-note">{ASKER_NOT_SHOWN_NOTE}</p>
      ) : null}

      {list.error ? <ErrorBlock failure={list.error} onRetry={list.reload} /> : null}
      {!list.error && !rows ? <LoadingBlock label="承認待ちを読み込んでいます…" /> : null}
      {rows && rows.length === 0 && !list.hasMore ? (
        <EmptyBlock label="いまは承認待ちがありません。" />
      ) : null}

      {rows && rows.length > 0 ? (
        <DataTable label="承認待ちの一覧">
          <thead>
            <tr>
              <th scope="col">種類</th>
              <th scope="col">内容</th>
              <th scope="col">どなた・どのトーク</th>
              <th scope="col">受け取った日時</th>
              <th scope="col">
                <span className="admin-visually-hidden">この1件でできること</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>
                  <span className="admin-ai-badge admin-ai-badge-wrap">
                    {withBreakHints(pendingKindLabel(row.kind))}
                  </span>
                </td>
                <td className="ui-cell-wrap admin-ai-pending-text">
                  {row.title}
                  {row.excerpt !== '' && !sameText(row.excerpt, row.title) ? (
                    <small className="admin-ai-excerpt">{row.excerpt}</small>
                  ) : null}
                  {row.notes.map((note) => (
                    <small key={note} className="admin-ai-excerpt">
                      {note}
                    </small>
                  ))}
                </td>
                <td className={row.whoHidden ? undefined : 'ui-cell-soft'}>
                  {row.whoHidden ? (
                    <span aria-label={ASKER_NOT_SHOWN} title={ASKER_NOT_SHOWN}>
                      {ASKER_NOT_SHOWN_MARK}
                    </span>
                  ) : (
                    row.who
                  )}
                </td>
                <td className="ui-cell-date">{formatTableDateTime(row.receivedAt)}</td>
                <td>
                  <Link
                    className="ui-button ui-button-secondary ui-button-compact"
                    to={row.to}
                    state={{ from: '/ai/pending' }}
                  >
                    {row.actionLabel}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}

      <LoadMoreFooter list={list} />
    </article>
  );
}

/**
 * 種類の目印を、狭い画面では語の切れ目で2行に折り返せるようにする（issue #387）。
 * 「答えられなかった／質問」「「AI先輩に教える」／へのメッセージ」のように、語の途中では折らない。
 */
function withBreakHints(label: string): ReactNode {
  const parts = label.split(/(?<=」|った|した)(?=へ|質問|下書き)/u);
  return parts.map((part, index) => (
    <Fragment key={index}>
      {index > 0 ? <wbr /> : null}
      {part}
    </Fragment>
  ));
}

/** 管理画面から登録した答え（元になったメッセージが無い）の「どなた・どのトーク」。 */
const REGISTERED_IN_ADMIN = '管理画面で登録';

/**
 * 2つの抜き書きが同じ文か（片方が途中で切れていても同じと見る）。
 * 太字の1行目と同じ文を2行目にもう一度出さないため（#329-4）。
 */
function sameText(a: string, b: string): boolean {
  const strip = (text: string): string => text.replace(/…$/, '').trim();
  const x = strip(a);
  const y = strip(b);
  return x === y || x.startsWith(y) || y.startsWith(x);
}

/** 1本の並びで届いた1件を、一覧の1行にする（並びはサーバが決めた順のまま）。 */
function toRow(item: AiPendingItem): PendingRow {
  if (item.type === 'draft') {
    const entry = item.draft;
    return {
      key: `k:${entry.id}`,
      kind: pendingKindOf(entry),
      title: summarize(entry.question ?? entry.answer),
      excerpt: summarize(entry.source?.text ?? entry.answer),
      notes: sourceNotes(entry),
      // 元の投稿の無い答えは、管理画面で登録したもの（「どなたかは分かりません」と出さない・#329-4）
      who: entry.source
        ? whoAndWhere(entry.source.user_name ?? null, entry.source.channel_name ?? null, {
            fromOtherSite: entry.source.from_other_site === true,
          })
        : REGISTERED_IN_ADMIN,
      receivedAt: entry.source?.asked_at ?? entry.created_at,
      to: `/ai/knowledge/${entry.id}`,
      actionLabel: '開く',
    };
  }
  // 答えをまだ書いていない質問（下書きがある質問は、答えの行として出ている）
  const question = item.question;
  return {
    key: `q:${question.id}`,
    kind: 'question',
    title: summarize(question.question),
    excerpt: '',
    notes: [],
    who: ASKER_NOT_SHOWN,
    whoHidden: true,
    receivedAt: question.created_at,
    to: `/ai/questions/${question.id}`,
    actionLabel: '答えを書く',
  };
}
